(function () {
  if (window.__cartAutoGiftManager) return;

  const config = window.cartAutoGiftConfig || {};
  const variantId = Number(config.variantId) || 0;
  const threshold = Number(config.threshold) || 0;
  const enabled = config.enabled === true && variantId > 0 && threshold > 0;
  const giftProperty = '_cart_reward_gift';
  const giftPropertyValue = 'true';
  const cartMutationPattern = /\/cart\/(add|change|update|clear)(\.js)?(?:[?#]|$)/i;
  const sectionIds = ['cart-drawer', 'cart-icon-bubble', 'main-cart-items', 'main-cart-footer'];
  const nativeFetch = window.fetch.bind(window);

  let syncTimer;
  let syncRequest;
  let pendingSync = false;
  let retryAfter = 0;

  function route(path) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}${path}`.replace(/([^:]\/)\/+/g, '$1');
  }

  function isManagedGift(item) {
    return Number(item?.variant_id || item?.id) === variantId
      && item?.properties?.[giftProperty] === giftPropertyValue;
  }

  function qualifyingSubtotal(cart) {
    const subtotal = Number(cart?.items_subtotal_price) || 0;
    const giftTotal = Array.isArray(cart?.items)
      ? cart.items.reduce((total, item) => {
          return isManagedGift(item) ? total + (Number(item.final_line_price) || 0) : total;
        }, 0)
      : 0;
    return Math.max(subtotal - giftTotal, 0);
  }

  function requestBody(payload) {
    return JSON.stringify({
      ...payload,
      sections: sectionIds,
      sections_url: window.location.pathname,
    });
  }

  async function parseResponse(response) {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.description || data.message || `Cart request failed: ${response.status}`);
    }
    return data;
  }

  function replaceSection(sections, sectionId, selector) {
    const html = sections?.[sectionId];
    if (!html) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const source = parsed.querySelector(selector);
    const target = document.querySelector(selector);
    if (!source || !target) return;

    target.innerHTML = source.innerHTML;
  }

  function renderSections(sections) {
    if (!sections) return;

    replaceSection(sections, 'cart-drawer', '#CartDrawer');
    replaceSection(sections, 'cart-icon-bubble', '#cart-icon-bubble');
    replaceSection(sections, 'main-cart-items', '#main-cart-items');
    replaceSection(sections, 'main-cart-footer', '#main-cart-footer');

    const drawer = document.querySelector('cart-drawer');
    const overlay = drawer?.querySelector('#CartDrawer-Overlay');
    if (drawer && overlay) overlay.addEventListener('click', () => drawer.close());
  }

  async function getCart() {
    const response = await nativeFetch(route('cart.js'), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    return parseResponse(response);
  }

  async function addGift() {
    const response = await nativeFetch(route('cart/add.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: requestBody({
        items: [{
          id: variantId,
          quantity: 1,
          properties: { [giftProperty]: giftPropertyValue },
        }],
      }),
    });
    const result = await parseResponse(response);
    renderSections(result.sections);
  }

  async function updateGiftLines(updates) {
    const response = await nativeFetch(route('cart/update.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: requestBody({ updates }),
    });
    const result = await parseResponse(response);
    renderSections(result.sections);
  }

  async function reconcile(cart) {
    const giftLines = Array.isArray(cart?.items) ? cart.items.filter(isManagedGift) : [];
    const qualifies = qualifyingSubtotal(cart) >= threshold;

    if (!qualifies && giftLines.length) {
      const updates = {};
      giftLines.forEach((item) => { updates[item.key] = 0; });
      await updateGiftLines(updates);
      return true;
    }

    if (qualifies && giftLines.length === 0) {
      await addGift();
      return true;
    }

    if (qualifies && giftLines.length) {
      const updates = {};
      giftLines.forEach((item, index) => {
        const requiredQuantity = index === 0 ? 1 : 0;
        if (Number(item.quantity) !== requiredQuantity) updates[item.key] = requiredQuantity;
      });
      if (Object.keys(updates).length) {
        await updateGiftLines(updates);
        return true;
      }
    }

    return false;
  }

  function announce(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.toggle('is-empty', Number(cart?.item_count) === 0);
  }

  async function sync(cartData) {
    if (!enabled || Date.now() < retryAfter) return cartData || null;
    if (syncRequest) {
      pendingSync = true;
      return syncRequest;
    }

    syncRequest = (async () => {
      try {
        const cart = cartData?.items ? cartData : await getCart();
        const changed = await reconcile(cart);
        const currentCart = changed ? await getCart() : cart;
        announce(currentCart);
        retryAfter = 0;
        return currentCart;
      } catch (error) {
        retryAfter = Date.now() + 30000;
        console.warn('[Cart auto gift]', error.message || error);
        return null;
      } finally {
        syncRequest = null;
        if (pendingSync) {
          pendingSync = false;
          scheduleSync(80);
        }
      }
    })();

    return syncRequest;
  }

  function scheduleSync(delay = 80, cartData) {
    if (!enabled) return;
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(() => sync(cartData), delay);
  }

  function handleCartUpdate(event) {
    const cartData = event?.cartData || event?.detail?.cart || event?.detail;
    scheduleSync(60, cartData?.items ? cartData : undefined);
  }

  if (enabled && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, handleCartUpdate);
  }

  ['cart:refresh', 'cart:change', 'free-shipping:refresh'].forEach((eventName) => {
    document.addEventListener(eventName, handleCartUpdate);
  });

  window.fetch = function (...args) {
    const request = args[0];
    const requestUrl = typeof request === 'string' ? request : request?.url;
    const responsePromise = nativeFetch(...args);
    if (enabled && typeof requestUrl === 'string' && cartMutationPattern.test(requestUrl)) {
      responsePromise.then((response) => response.ok && scheduleSync()).catch(() => null);
    }
    return responsePromise;
  };

  const nativeXhrOpen = XMLHttpRequest.prototype.open;
  const nativeXhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url, ...args) {
    this.__isAutoGiftCartMutation = enabled && typeof url === 'string' && cartMutationPattern.test(url);
    return nativeXhrOpen.call(this, method, url, ...args);
  };
  XMLHttpRequest.prototype.send = function (...args) {
    if (this.__isAutoGiftCartMutation) {
      this.addEventListener('loadend', () => {
        if (this.status >= 200 && this.status < 400) scheduleSync();
      }, { once: true });
    }
    return nativeXhrSend.apply(this, args);
  };

  window.__cartAutoGiftManager = {
    enabled,
    threshold,
    variantId,
    refresh: scheduleSync,
    sync,
  };

  if (enabled) scheduleSync(0);
})();
